import React, { useState, useEffect } from 'react';
import { createShift } from '../../services/rosterService';
import apiClient from '../../services/api';
import { DatePicker } from '../ui/DatePicker';

interface CreateShiftModalProps {
    onClose: () => void;
    onSuccess: () => void;
    initialDate?: Date;
    versionId?: string;
}

interface User {
    id: string;
    name: string;
    email: string;
    isAgency?: boolean;
    agencyName?: string;
    role: {
        name: string;
    };
    userSkills?: Array<{
        skill: {
            id: string;
            name: string;
        };
    }>;
}

interface Role {
    id: string;
    name: string;
}

interface Skill {
    id: string;
    name: string;
    description?: string;
}

const CreateShiftModal: React.FC<CreateShiftModalProps> = ({ onClose, onSuccess, initialDate, versionId }) => {
    const [formData, setFormData] = useState({
        userId: '',
        date: initialDate ? initialDate.toISOString().split('T')[0] : '',
        startTime: '07:30',
        endTime: '18:30',
        type: '',
        location: '',
        notes: ''
    });
    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [error, setError] = useState('');
    const [warnings, setWarnings] = useState<string[]>([]);

    // Temp staff inline creation
    const [showTempForm, setShowTempForm] = useState(false);
    const [tempName, setTempName] = useState('');
    const [tempUsername, setTempUsername] = useState('');
    const [tempEmail, setTempEmail] = useState('');
    const [tempAgencyName, setTempAgencyName] = useState('');
    const [tempSkills, setTempSkills] = useState<string[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [creatingTemp, setCreatingTemp] = useState(false);
    const [tempSuccess, setTempSuccess] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await apiClient.get('/users', {
                params: { limit: 100 },
            });
            setUsers(response.data.data || response.data.users || []);
        } catch (error) {
            console.error('Failed to load users', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadTempFormData = async () => {
        try {
            const [skillsRes, rolesRes] = await Promise.all([
                apiClient.get('/users/skills/list'),
                apiClient.get('/roles'),
            ]);
            setAvailableSkills(skillsRes.data.data || []);
            const allRoles = rolesRes.data.data || [];
            setRoles(allRoles);
            // Default to Clearer role
            const clearer = allRoles.find((r: Role) => r.name.toUpperCase().includes('CLEARER'));
            if (clearer) setSelectedRoleId(clearer.id);
            else if (allRoles.length > 0) setSelectedRoleId(allRoles[0].id);
        } catch (err) {
            console.error('Failed to load form data', err);
        }
    };

    const handleOpenTempForm = () => {
        setShowTempForm(true);
        setTempName('');
        setTempUsername('');
        setTempEmail('');
        setTempAgencyName('');
        setTempSkills([]);
        setTempSuccess('');
        loadTempFormData();
    };

    const handleCreateTemp = async () => {
        if (!tempName.trim()) {
            setError('Please enter the temp staff name');
            return;
        }
        if (!selectedRoleId) {
            setError('No role available');
            return;
        }

        setCreatingTemp(true);
        setError('');

        const nameParts = tempName.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join('') || '';
        const loginName = `${firstName}${lastName}T1`.replace(/[^a-zA-Z0-9._-]/g, '');
        const email = tempEmail.trim() || `${loginName}@eagle.local`;
        const username = tempUsername.trim() || loginName;

        try {
            const createRes = await apiClient.post('/users', {
                name: tempName.trim(),
                username,
                email,
                password: 'apple100',
                roleId: selectedRoleId,
                skillIds: tempSkills,
                isAgency: true,
                agencyName: tempAgencyName.trim() || 'Temp Agency',
            });

            const newUser = createRes.data.data;
            if (newUser) {
                await loadUsers();
                setFormData(prev => ({ ...prev, userId: newUser.id }));
                setTempSuccess(`"${tempName}" added — login: ${username}`);
                setShowTempForm(false);
            }
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.response?.data?.error?.details || 'Failed to create temp staff');
        } finally {
            setCreatingTemp(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const newFormData = { ...formData, [e.target.name]: e.target.value };
        setFormData(newFormData);
        
        // Clear temp success message when user changes selection
        if (e.target.name === 'userId') setTempSuccess('');

        // Validate role for shift type
        if (e.target.name === 'type' || e.target.name === 'userId') {
            const selectedUser = users.find(u => u.id === newFormData.userId);
            if (selectedUser && newFormData.type) {
                const role = selectedUser.role.name.toUpperCase();
                const warningsList: string[] = [];
                
                if (newFormData.type === 'CLEARER' && role !== 'CLEARER') {
                    warningsList.push('Clearer shifts typically require Clearer role');
                }
                if (newFormData.type === 'CONTROL' && role !== 'CONTROLLER') {
                    warningsList.push('Control shifts typically require Controller role');
                }
                
                setWarnings(warningsList);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        
        try {
            if (!versionId) {
                setError('Create or select a draft roster for this calendar month before adding shifts.');
                return;
            }

            const start = new Date(`${formData.date}T${formData.startTime}:00`);
            const end = new Date(`${formData.date}T${formData.endTime}:00`);

            if (end <= start) {
                setError('End time must be after start time');
                return;
            }

            await createShift({
                userId: formData.userId,
                startTime: start.toISOString(),
                endTime: end.toISOString(),
                type: formData.type,
                location: formData.location || undefined,
                notes: formData.notes,
                versionId,
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to create shift');
        }
    };

    // Compute allowed shift types based on selected user's skills
    const ALL_SHIFT_TYPES = [
        { value: 'LHR', label: 'LHR Clearer (Heathrow)' },
        { value: 'CLEARER', label: 'LGW Clearer (Gatwick)' },
        { value: 'CONTROL', label: 'Control' },
        { value: 'OFFICE', label: 'Office' },
        { value: 'WAREHOUSE', label: 'Warehouse' },
    ];

    const getShiftTypesForUser = (user: User | undefined) => {
        if (!user || !user.userSkills || user.userSkills.length === 0) {
            return ALL_SHIFT_TYPES; // Show all if no skills data
        }
        const skillNames = user.userSkills.map(us => us.skill.name.toLowerCase());
        const allowed: typeof ALL_SHIFT_TYPES = [];

        // LHR Clearer: needs 'lhr full pass' or 'agency full pass'
        if (skillNames.some(s => s.includes('lhr full pass') || s.includes('agency full pass'))) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'LHR')!);
        }
        // LGW Clearer: needs 'lgw full pass'
        if (skillNames.some(s => s.includes('lgw full pass'))) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'CLEARER')!);
        }
        // Control: needs 'control training' or 'controlling'
        if (skillNames.some(s => s.includes('control training') || s === 'controlling')) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'CONTROL')!);
        }
        // Office: needs 'office training' or 'office'
        if (skillNames.some(s => s.includes('office training') || s === 'office')) {
            allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'OFFICE')!);
        }
        // Warehouse: always available
        allowed.push(ALL_SHIFT_TYPES.find(t => t.value === 'WAREHOUSE')!);

        return allowed.length > 0 ? allowed : ALL_SHIFT_TYPES;
    };

    const selectedUser = users.find(u => u.id === formData.userId);
    const allowedShiftTypes = getShiftTypesForUser(selectedUser);

    // Auto-select first allowed shift type when user changes
    useEffect(() => {
        if (formData.userId && allowedShiftTypes.length > 0) {
            const currentTypeAllowed = allowedShiftTypes.some(t => t.value === formData.type);
            if (!currentTypeAllowed) {
                setFormData(prev => ({ ...prev, type: allowedShiftTypes[0].value }));
            }
        }
    }, [formData.userId]);

    // Separate regular and temp users
    const regularUsers = users.filter(u => !u.isAgency);
    const tempUsers = users.filter(u => u.isAgency);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-[440px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <h3 className="text-lg font-bold text-slate-800">Create Shift</h3>
                    <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors">
                        <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}
                    {tempSuccess && (
                        <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm font-medium flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {tempSuccess}
                        </div>
                    )}

                    {/* Staff Member */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Staff Member *</label>
                        {loadingUsers ? (
                            <div className="text-sm text-slate-400 py-2">Loading staff...</div>
                        ) : (
                            <>
                                <select
                                    name="userId"
                                    value={formData.userId}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                                    required
                                >
                                    <option value="">Select staff member</option>
                                    {regularUsers.length > 0 && (
                                        <optgroup label="Staff">
                                            {regularUsers.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name} ({user.role.name})
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {tempUsers.length > 0 && (
                                        <optgroup label="⏱ Temp Agency Staff">
                                            {tempUsers.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name} ({user.role.name}) — {user.agencyName || 'Temp'}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>

                                {/* Add Temp Staff Button */}
                                {!showTempForm && (
                                    <button
                                        type="button"
                                        onClick={handleOpenTempForm}
                                        className="mt-2 w-full px-3 py-2 text-sm font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                        + Add New Temp Staff
                                    </button>
                                )}
                            </>
                        )}

                        {/* Inline Temp Staff Creation */}
                        {showTempForm && (
                            <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                        New Temp Staff
                                    </h4>
                                    <button type="button" onClick={() => setShowTempForm(false)} className="text-orange-400 hover:text-orange-600">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-orange-700 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={tempName}
                                        onChange={e => setTempName(e.target.value)}
                                        placeholder="e.g., John Smith"
                                        className="w-full px-3 py-2 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white"
                                        autoFocus
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 mb-1">Username</label>
                                        <input
                                            type="text"
                                            value={tempUsername}
                                            onChange={e => setTempUsername(e.target.value)}
                                            placeholder="Auto if blank"
                                            className="w-full px-3 py-2 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 mb-1">Email</label>
                                        <input
                                            type="email"
                                            value={tempEmail}
                                            onChange={e => setTempEmail(e.target.value)}
                                            placeholder="Auto if blank"
                                            className="w-full px-3 py-2 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-orange-700 mb-1">Agency Name</label>
                                    <input
                                        type="text"
                                        value={tempAgencyName}
                                        onChange={e => setTempAgencyName(e.target.value)}
                                        placeholder="Optional"
                                        className="w-full px-3 py-2 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-orange-700 mb-1">Role</label>
                                    <select
                                        value={selectedRoleId}
                                        onChange={e => setSelectedRoleId(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-orange-200 focus:border-orange-400 outline-none text-sm bg-white"
                                    >
                                        {roles.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Skills */}
                                {availableSkills.length > 0 && (
                                    <div>
                                        <label className="block text-xs font-semibold text-orange-700 mb-1">Skills</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {availableSkills.map(skill => (
                                                <button
                                                    key={skill.id}
                                                    type="button"
                                                    onClick={() => setTempSkills(prev => prev.includes(skill.id) ? prev.filter(id => id !== skill.id) : [...prev, skill.id])}
                                                    className={`px-2 py-1 rounded-md text-xs font-semibold transition-all border ${
                                                        tempSkills.includes(skill.id)
                                                            ? 'bg-orange-200 border-orange-400 text-orange-900'
                                                            : 'bg-white border-orange-200 text-orange-600 hover:bg-orange-100'
                                                    }`}
                                                >
                                                    {tempSkills.includes(skill.id) && '✓ '}{skill.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={handleCreateTemp}
                                    disabled={creatingTemp || !tempName.trim()}
                                    className="w-full px-3 py-2.5 text-sm font-bold bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {creatingTemp ? (
                                        <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> Creating...</>
                                    ) : (
                                        'Create & Select'
                                    )}
                                </button>
                            </div>
                        )}

                        {warnings.length > 0 && (
                            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                                {warnings.map((warning, idx) => (
                                    <div key={idx} className="text-xs text-yellow-700">{warning}</div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Date */}
                    <div>
                        <DatePicker
                            label="Date *"
                            value={formData.date}
                            onChange={(v) => setFormData(prev => ({ ...prev, date: v }))}
                            placeholder="Pick shift date"
                        />
                    </div>

                    {/* Times */}
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Start</label>
                            <input type="time" name="startTime" value={formData.startTime} onChange={handleChange} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm" required />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">End</label>
                            <input type="time" name="endTime" value={formData.endTime} onChange={handleChange} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm" required />
                        </div>
                    </div>

                    {/* Shift Type */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Shift Type *</label>
                        {!formData.userId ? (
                            <div className="text-xs text-slate-400 py-2 px-3 bg-slate-50 rounded-xl border border-slate-200">Select a staff member first to see available shift types</div>
                        ) : (
                            <select name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm">
                                {allowedShiftTypes.map(st => (
                                    <option key={st.value} value={st.value}>{st.label}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Location */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Location</label>
                        <select name="location" value={formData.location} onChange={handleChange} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm">
                            <option value="">None</option>
                            <option value="Heathrow">Heathrow</option>
                            <option value="LGW">LGW</option>
                            <option value="Office">Office</option>
                            <option value="Warehouse">Warehouse</option>
                        </select>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Notes</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                            rows={2}
                            placeholder="Optional notes..."
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" className="flex-1 px-4 py-2.5 text-sm font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all">
                            Create Shift
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateShiftModal;
